<template>
  <div id="app">
    <!-- Navigation Bar -->
    <nav v-if="!$route.meta.hideLayout" class="navbar navbar-expand-lg navbar-dark bg-dark mb-4">
      <div class="container">
        <!-- Brand: Links to Orders if logged in, Login if not -->
        <RouterLink
          class="navbar-brand fw-semibold"
          :to="isAuthenticated ? '/dashboard' : '/login'"
        >
          Refurbish Sheet Management System
        </RouterLink>

        <!-- Navbar toggler for mobile view -->
        <button
          class="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#mainNavbar"
          aria-controls="mainNavbar"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span class="navbar-toggler-icon"></span>
        </button>

        <!-- Navbar links -->
        <div class="collapse navbar-collapse" id="mainNavbar">
          <ul class="navbar-nav ms-auto">
            <!-- Dashboard link -->
            <li class="nav-item" v-if="isAuthenticated">
              <RouterLink class="nav-link" active-class="active" to="/dashboard">
                Dashboard
              </RouterLink>
            </li>

            <!-- Orders link: visible only if authenticated -->
            <li class="nav-item dropdown" v-if="isAuthenticated">
              <a class="nav-link dropdown-toggle" href="#" role="button" data-bs-toggle="dropdown"
                aria-expanded="false">
                Devices
              </a>
              <ul class="dropdown-menu dropdown-menu-end">
                <li>
                  <RouterLink class="dropdown-item" to="/orders">
                    View Devices
                  </RouterLink>
                </li>
                <li>
                  <RouterLink class="dropdown-item" to="/orders/new">
                    New Device
                  </RouterLink>
                </li>
                <li>
                  <RouterLink class="dropdown-item" to="/orders/batch">
                    Batch Create
                  </RouterLink>
                </li>
                <li v-if="isSupervisor">
                  <RouterLink class="dropdown-item" to="/import">
                    Import CSV
                  </RouterLink>
                </li>
                <li>
                  <RouterLink class="dropdown-item" to="/issues">
                    Issues
                  </RouterLink>
                </li>
                <li v-if="isSupervisor">
                  <RouterLink class="dropdown-item" to="/sales-queue">
                    Sales Queue
                  </RouterLink>
                </li>
                <li>
                  <RouterLink class="dropdown-item" to="/reports">
                    Reporting
                  </RouterLink>
                </li>
                <li v-if="isSupervisor">
                  <RouterLink class="dropdown-item" to="/activity-log">
                    Activity Log
                  </RouterLink>
                </li>
              </ul>
            </li>

            <!-- Device Models -->
            <li class="nav-item" v-if="isAuthenticated">
              <RouterLink class="nav-link" active-class="active" to="/device-models">
                Models
              </RouterLink>
            </li>

            <!-- Customers link -->
            <li class="nav-item" v-if="isAuthenticated">
              <RouterLink class="nav-link" active-class="active" to="/customers">
                Customers
              </RouterLink>
            </li>

            <!-- Templates dropdown: visible only if authenticated -->
            <li class="nav-item dropdown" v-if="isAuthenticated">
              <a
                class="nav-link dropdown-toggle"
                href="#"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                Templates
              </a>
              <ul class="dropdown-menu dropdown-menu-end">
                <!-- View Templates: visible to all authenticated users -->
                <li>
                  <RouterLink class="dropdown-item" to="/templates">
                    View Templates
                  </RouterLink>
                </li>
                <!-- Create Template: visible only to supervisors/admins -->
                <li v-if="isSupervisor">
                  <RouterLink class="dropdown-item" to="/templates/create">
                    Create a new Template
                  </RouterLink>
                </li>
              </ul>
            </li>

            <!-- Login/Signup links: visible only if not authenticated -->
            <li class="nav-item" v-if="!isAuthenticated">
              <RouterLink class="nav-link" active-class="active" to="/login">
                Login
              </RouterLink>
            </li>
            <li class="nav-item" v-if="!isAuthenticated">
              <RouterLink class="nav-link" active-class="active" to="/signup">
                Sign Up
              </RouterLink>
            </li>

            <!-- Role label: shows current user's role if authenticated -->
            <li
              class="nav-item d-flex align-items-center ms-3"
              v-if="isAuthenticated && roleLabel"
            >
              <span class="navbar-text text-white-50">
                {{ roleLabel }}
              </span>
            </li>

            <!-- Logout button: visible only if authenticated -->
            <li class="nav-item ms-3" v-if="isAuthenticated">
              <button class="btn btn-outline-light btn-sm" @click="logout">
                Log out
              </button>
            </li>
          </ul>
        </div>
      </div>
    </nav>

    <!-- Main content rendered by router -->
    <router-view />
  </div>
</template>

<script>
export default {
  name: "App",
  data() {
    return {
      // Holds the current user object, or null if not logged in
      currentUser: null,
    };
  },
  created() {
    // Load authentication info from localStorage when component is created
    this.loadAuthFromStorage();
  },
  watch: {
    // Watch for route changes to reload user/auth info (e.g., after login)
    $route() {
      this.loadAuthFromStorage();
    },
  },
  computed: {
    // Returns true if a user is authenticated
    isAuthenticated() {
      return !!this.currentUser;
    },
    // Returns the user's role label, or empty string if not set
    roleLabel() {
      return this.currentUser && this.currentUser.role
        ? this.currentUser.role
        : "";
    },
    // Returns true if the user is a supervisor or admin
    isSupervisor() {
      const role = (this.currentUser?.role || "").toLowerCase();
      return role === "supervisor" || role === "admin";
    },
  },
  methods: {
    // Loads the current user from localStorage
    loadAuthFromStorage() {
      const raw = localStorage.getItem("currentUser");
      try {
        this.currentUser = raw ? JSON.parse(raw) : null;
      } catch {
        this.currentUser = null;
      }
    },
    // Logs out the user and clears authentication info
    logout() {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("currentUser");

      // Clear reactive state so navbar updates immediately
      this.currentUser = null;

      // Redirect to login page
      this.$router.push({ name: "Login" });
    },
  },
};
</script>
